/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { DataServiceRequest } from './DataServiceRequest';
import { IDataServiceOptions, IDataServiceQueryInternal, IDataServiceRequest, IQueryResultSettings } from './Interfaces';

/**
 * Represents a data service query object.
 */
export class DataServiceQuery {
    private _query: IDataServiceQueryInternal;

    /**
     * Creates a new instance of DataServiceQuery<T>.
     * @param {IDataServiceRequestFactory} dataServiceRequestFactory The data service request factory.
     * @param {string} [entitySet] The entity set name.
     * @param {string} [entityType] The entity type name.
     * @param {any} [returntype] The return type of query.
     * @param {any} [key] Entity key.
     */
    constructor(entitySet?: string, entityType?: string, returnType?: any, key?: any) {
        this._query = {
            entitySet,
            entityType,
            key,
            returnType,
            tokens: {},
            // by default all action will skip reading from cache before making execute call
            options: { bypassCache: 'get', returnEntity: entityType },
            resultSettings: { Paging: {}, Sorting: {} }
        };
    }

    /**
     * Query result settings.
     *
     * @param {string} value The value of query result settings.
     */
    public resultSettings(value: IQueryResultSettings): DataServiceQuery {
        if (!value) {
            value = {};
        }

        this._query.resultSettings = {};
        this._query.resultSettings.Paging = value.Paging || {};
        this._query.resultSettings.Sorting = value.Sorting || {};
        this._query.resultSettings.count = value.count;

        return this;
    }

    /**
     * Filter Clause.
     * @param {string} value The filter value.
     */
    public filter(value: string): DataServiceQuery {
        this._query.filterSettings = value;

        return this;
    }

    /**
     * Top clause
     * @param {string} value The number of top rows to select.
     */
    public top(value: number): DataServiceQuery {
        this._query.resultSettings.Paging.Top = value;

        return this;
    }

    /**
     * Skip clause
     * @param {string} value The number of rows to skip
     */
    public skip(value: number): DataServiceQuery {
        this._query.resultSettings.Paging.Skip = value;

        return this;
    }

    /**
     * Expand clause
     * @param {string} propertyName The property name to expand.
     */
    public expand(propertyName: string): DataServiceQuery {
        if (this._query.expands == null) {
            this._query.expands = [];
        }

        this._query.expands.push(propertyName);
        return this;
    }

    /**
     * Request number of total row available.
     */
    public inlineCount(): DataServiceQuery {
        this._query.inlineCount = true;

        return this;
    }

    /**
     * Order by clause.
     * @param {string} value The field name for ordering.
     */
    public orderBy(value: string): DataServiceQuery {
        if (!this._query.resultSettings.Sorting.Columns) {
            this._query.resultSettings.Sorting.Columns = [];
        }

        this._query.resultSettings.Sorting.Columns.push({ ColumnName: value });

        return this;
    }

    /**
     * Transforms the request into a create request.
     * @param {string} object The object.
     * @return {IDataServiceRequest} The data service request.
     */
    public create(object: any): IDataServiceRequest {
        this._query.action = 'Create';
        this._query.data = this._createODataParameters(object);
        this._query.options.bypassCache = 'none';
        return this._createRequest();
    }

    /**
     * Transforms the request into a read request.
     * @return {IDataServiceRequest} The data service request.
     */
    public read(): IDataServiceRequest {
        if (this._query.key || (this._query.entitySet === 'Customers' && this._query.key !== null && this._query.key !== undefined)) {
            this._query.action = 'Read';
        } else {
            this._query.action = 'ReadAll';
            this._query.isReturnTypeACollection = true;
        }

        this._query.options.bypassCache = 'none';
        return this._createRequest();
    }

    /**
     * Transforms the request into an update request.
     * @param {string} object The object.
     * @return {IDataServiceRequest} The data service request.
     */
    public update(object: any): IDataServiceRequest {
        this._query.action = 'Update';
        this._query.data = this._createODataParameters(object);

        return this._createRequest();
    }

    /**
     * Transforms the request into a delete request.
     * @return {DataServiceRequest} The data service request.
     */
    public delete(): IDataServiceRequest {
        this._query.action = 'Delete';

        return this._createRequest();
    }

    public setGetAllRecords(): void {
        this._query.resultSettings.Paging.Top = -1;
    }

    public isGetAllRecords(): boolean {
        return this._query.resultSettings.Paging.Top === -1;
    }

    public createDataServiceRequestForOperation(
        operationName: string,
        isAction: boolean,
        returnType: any,
        isReturnTypeACollection: string,
        options: IDataServiceOptions,
        data?: unknown
    ): IDataServiceRequest {
        this._query.returnType = returnType;
        this._query.isAction = isAction;
        this._query.isReturnTypeACollection = isReturnTypeACollection === 'true';
        this._query.action = operationName;
        this._query.data = this._createODataParameters(data);
        this._query.options = { ...{ bypassCache: 'get', returnEntity: 'CUSTOM-ENTITY' }, ...options };

        if (this._query.isReturnTypeACollection && !this._query.resultSettings.Paging.Top) {
            // for paginated requests, where we didn't specify page size, set to fetch all records
            this.setGetAllRecords();
        }

        return this._createRequest();
    }

    private _createODataParameters(data: unknown) {
        return data ? { parameters: data } : undefined;
    }

    private _createRequest(): IDataServiceRequest {
        return new DataServiceRequest(this._query);
    }
}
